import { fetchApi } from "@/lib/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export type InternalOpsChannelKind = "direct" | "cohort" | "group";

export interface InternalOpsChannel {
  id: string;
  sdk_url: string;
  kind: InternalOpsChannelKind;
  cohort_key: string | null;
  channel_display_name: string | null;
  scope: { groups?: string[] };
  created_at: string;
  updated_at: string;
  members?: Array<{
    user_id: string;
    joined_at: string;
    user?: InternalOpsChannelMemberUser;
  }>;
}

export interface InternalOpsChannelMemberUser {
  id: string;
  name: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  role?: string;
  is_supervisor?: boolean;
  system_roles?: string[];
  pronouns?: string | null;
  location?: string | null;
  profile_picture_url?: string | null;
  created_at?: string;
  deactivated?: boolean;
}

export interface InternalOpsMessage {
  id: string;
  sdk_id: string;
  internal_ops_channel_id: string;
  from_user: { id: string; name?: string };
  message: string;
  custom_type: string | null;
  data: string | null;
  delivered_at: string;
  created_at: string;
  metadata: Record<string, unknown>;
}

export type RecipientSuggestion =
  | {
      type: "user";
      id: string;
      name: string;
      role?: string;
      avatar_url?: string;
      last_interacted_at?: string | null;
    }
  | {
      type: "cohort";
      id: string;
      name: string;
      cohort_key?: string;
      last_interacted_at?: string | null;
    };

export interface InternalChannelStats {
  channelId: string;
  kind: "cohort" | "group";
  displayName: string | null;
  memberCount: number;
  ageRange: { min: number; max: number } | null;
  avgTenureMonths: number | null;
  stateCount: number;
  groups: string[];
}

const NONE = "__none__";

export const internalOpsKeys = {
  all: ["internal-ops"] as const,
  channels: () => [...internalOpsKeys.all, "channels"] as const,
  channel: (id: string | null | undefined) =>
    [...internalOpsKeys.channels(), id ?? NONE] as const,
  channelStats: (id: string | null | undefined) =>
    [...internalOpsKeys.channel(id), "stats"] as const,
  recipients: (query: string) =>
    [...internalOpsKeys.all, "recipients", query] as const,
  findByRecipients: (recipientIds: string[]) =>
    [
      ...internalOpsKeys.all,
      "find-by-recipients",
      [...recipientIds].sort(),
    ] as const,
};

export const useInternalOpsChannels = (enabled?: boolean) =>
  useQuery({
    queryKey: internalOpsKeys.channels(),
    queryFn: async () => {
      const { data } = await fetchApi<{ channels: InternalOpsChannel[] }>({
        url: "/v2/internal-ops/channels",
        method: "GET",
      });
      return data.channels;
    },
    enabled,
  });

export const useInternalChannelStats = (channelId: string | null | undefined) =>
  useQuery({
    queryKey: internalOpsKeys.channelStats(channelId),
    queryFn: async () => {
      const { data } = await fetchApi<{ stats: InternalChannelStats }>({
        url: `/v2/internal-ops/channels/${channelId}/stats`,
        method: "GET",
      });
      return data.stats;
    },
    enabled: !!channelId,
    staleTime: 5 * 60_000,
  });

export const useInternalOpsChannel = (channelId: string | null | undefined) =>
  useQuery({
    queryKey: internalOpsKeys.channel(channelId),
    queryFn: async () => {
      const { data } = await fetchApi<{
        channel: InternalOpsChannel;
      }>({
        url: `/v2/internal-ops/channels/${channelId}`,
        method: "GET",
      });
      return data.channel;
    },
    enabled: !!channelId,
  });

/**
 * Creates (or returns the existing) internal ops channel for the given set of
 * recipients. Backend dedupes by exact member set, so 1:1 and N-party reopens
 * surface the existing thread + history.
 */
export const useCreateInternalOpsChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      recipientIds: string[];
      displayName?: string;
    }) => {
      const { data } = await fetchApi<{ channel: InternalOpsChannel }>({
        url: "/v2/internal-ops/channels",
        method: "POST",
        data: params,
      });
      return data.channel;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalOpsKeys.channels(),
      });
      queryClient.invalidateQueries({
        queryKey: [...internalOpsKeys.all, "find-by-recipients"],
      });
    },
  });
};

export const useRenameInternalOpsGroupChannel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: { channelId: string; displayName: string }) => {
      const { data } = await fetchApi<{ channel: InternalOpsChannel }>({
        url: `/v2/internal-ops/channels/${params.channelId}`,
        method: "PATCH",
        data: { displayName: params.displayName },
      });
      return data.channel;
    },
    onSuccess: (channel) => {
      queryClient.invalidateQueries({
        queryKey: internalOpsKeys.channels(),
      });
      queryClient.invalidateQueries({
        queryKey: internalOpsKeys.channel(channel.id),
      });
      queryClient.invalidateQueries({
        queryKey: internalOpsKeys.channelStats(channel.id),
      });
    },
  });
};

const customTypeFromFile = (file: File): string => {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("audio/")) return "VOICE_MESSAGE";
  if (mime.startsWith("image/")) return "IMAGE_MESSAGE";
  if (mime.startsWith("video/")) return "VIDEO_MESSAGE";
  return "MEDIA";
};

/**
 * Mirrors `saveSentMessage` in `lib/api/messages.ts`: text messages send JSON,
 * file messages send multipart/form-data with the binary in the `data` field
 * so the backend's `upload.single('data')` middleware can push it to S3.
 */
export const useSaveInternalOpsMessage = () => {
  return useMutation({
    mutationFn: async (params: {
      channelId: string;
      sdkId: string;
      message: string;
      // Sendbird message_type
      messageType?: "MESG" | "ADMM" | "FILE";
      customType?: string;
      file?: File | null;
      data?: string;
    }) => {
      const isFileUpload = !!params.file;
      const normalizedCustomType = params.customType?.trim();
      const customType =
        (normalizedCustomType && normalizedCustomType.length > 0
          ? normalizedCustomType
          : undefined) ??
        (params.file ? customTypeFromFile(params.file) : "TEXT");
      const messageType =
        params.messageType ?? (isFileUpload ? "FILE" : "MESG");

      if (isFileUpload && params.file) {
        const formData = new FormData();
        formData.append("sdkId", params.sdkId);
        formData.append("message", params.message);
        formData.append("messageType", messageType);
        formData.append("customType", customType);
        formData.append("data", params.file);

        const { data } = await fetchApi<{ message: InternalOpsMessage }>({
          url: `/v2/internal-ops/channels/${params.channelId}/messages`,
          method: "POST",
          headers: { "Content-Type": "multipart/form-data" },
          data: formData,
        });
        return data.message;
      }

      const { data } = await fetchApi<{ message: InternalOpsMessage }>({
        url: `/v2/internal-ops/channels/${params.channelId}/messages`,
        method: "POST",
        data: {
          sdkId: params.sdkId,
          message: params.message,
          messageType,
          customType,
          data: params.data,
        },
      });
      return data.message;
    },
  });
};

/**
 * Looks up an existing internal ops channel whose member set matches the given
 * recipients (plus the current user). Returns null when no exact match exists.
 */
export const useFindInternalOpsChannelByRecipients = (recipientIds: string[]) =>
  useQuery({
    queryKey: internalOpsKeys.findByRecipients(recipientIds),
    queryFn: async () => {
      const { data } = await fetchApi<{ channel: InternalOpsChannel | null }>({
        url: "/v2/internal-ops/channels/find",
        method: "POST",
        data: { recipientIds },
      });
      return data.channel ?? null;
    },
    enabled: recipientIds.length > 0,
    staleTime: 30_000,
  });

export const useRecipientSuggestions = (
  query: string,
  options: { enabled?: boolean; limit?: number } = {},
) =>
  useQuery({
    queryKey: internalOpsKeys.recipients(query),
    queryFn: async () => {
      const { data } = await fetchApi<{
        suggestions: RecipientSuggestion[];
      }>({
        url: `/v2/internal-ops/recipients/suggest`,
        method: "GET",
        params: { query, limit: options.limit ?? 5 },
      });
      return data.suggestions;
    },
    enabled: options.enabled ?? true,
    staleTime: 60_000,
  });

export const useSendInternalOpsDirectMessage = (recipientId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...internalOpsKeys.all, "direct-message", recipientId],
    mutationFn: async ({ message }: { message: string }) => {
      const { data } = await fetchApi<{
        message: InternalOpsMessage;
        channelId: string;
      }>({
        url: `/v2/internal-ops/direct-messages/${encodeURIComponent(recipientId)}`,
        method: "POST",
        data: { message },
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: internalOpsKeys.channels(),
      });
      queryClient.invalidateQueries({
        queryKey: [...internalOpsKeys.all, "find-by-recipients"],
      });
    },
  });
};
